import { AuthFetchWithResponse } from 'src/Types/api'
import { ApiResult } from 'src/Libs/api'
import { InstallationFile, InstallationStatement } from 'src/Libs/documents/models/file.model'
import { StatusCodes } from 'src/Libs/http-status-codes'
import { UnexpectedApiResponseError } from 'src/Libs/errors'
import { FetchedArrayDataType, FetchedObjectApiDataType } from '@e3dc-react/shell/Hooks/useFetch'
import { objectApiResult } from '@e3dc-react/shell/Libs/ApiHelpers'
import { AppLang } from '@e3dc-react/shell/State/App/AppModel'
import { handleFetchError } from 'src/Libs/api/utils'

/**
 * Returns a list of installation files per system
 * @param authFetch
 * @param installationId
 * @param storageId
 */
export const fetchInstallationDocuments = async (
  installationId: number,
  storageId: string,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<InstallationFile[]>> => {
  try {
    const response = await authFetch<FetchedObjectApiDataType>({
      path: `installations/${installationId}/${storageId}/files`,
      method: 'GET',
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
    })
    if (response.status === StatusCodes.OK) {
      const arrayValues = objectApiResult<FetchedObjectApiDataType, FetchedArrayDataType>(response.data as FetchedObjectApiDataType)
      return new ApiResult<InstallationFile[]>(arrayValues as InstallationFile[])
    }

    return new ApiResult<InstallationFile[]>(null, true)
  } catch (error) {
    return handleFetchError<InstallationFile[]>(error)
  }
}

export const fetchOneInstallationDocument = async (
  installationId: number | undefined,
  fileId: number,
  authFetch: AuthFetchWithResponse,
  isWebview: boolean
): Promise<ApiResult<string>> => {
  try {
    const response = await authFetch<BlobPart>({
      path: `installations/${installationId}/files/${fileId}/file`,
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      method: 'GET',
      type: isWebview ? 'arraybuffer' : 'blob',
    })
    if (response.status !== StatusCodes.OK) {
      throw new UnexpectedApiResponseError(response.status)
    }
    return new ApiResult<string>(response.data as string, false)
  } catch (error) {
    return handleFetchError<string>(error)
  }
}

export const fetchGappReport = async (
  storageId: string | undefined,
  language: AppLang,
  authFetch: AuthFetchWithResponse,
  isWebview: boolean
): Promise<ApiResult<string>> => {
  if (!storageId) {
    return new ApiResult<string>(null, true, 'storageId is undefined')
  }

  try {
    const response = await authFetch<BlobPart>({
      path: `storages/${storageId}/gapp-reports/${language}`,
      method: 'GET',
      type: isWebview ? 'arraybuffer' : 'blob',
    })

    if (response.status !== StatusCodes.OK) {
      return new ApiResult<string>(null, true)
    }

    return new ApiResult<string>(response.data as string, false)
  } catch (error) {
    return handleFetchError<string>(error)
  }
}

/**
 * Fetch the current installation statement (inbetriebnahmeerklärung)
 * @param installationId
 * @param authFetch
 * @returns the installation statement
 */
export const fetchInstallationStatement = async (
  deviceLinkId: number,
  installationId: number,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<InstallationStatement>> => {
  try {
    const response = await authFetch<InstallationStatement>({
      path: `installations/${installationId}/device-links/${deviceLinkId}/statements/current`,
      method: 'GET',
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
    })
    if (response.status !== StatusCodes.OK && response.status !== StatusCodes.NO_CONTENT) {
      throw new UnexpectedApiResponseError(response.status)
    }
    return new ApiResult<InstallationStatement>(response.data as InstallationStatement, false)
  } catch (error) {
    return handleFetchError<InstallationStatement>(error)
  }
}

/**
 * Fetch the current installation statement (inbetriebnahmeerklärung) document
 * @param installationId
 * @param authFetch
 * @returns the installation statement
 */

export const uploadInstallationDocument = async (installationId: number, fileObject: FormData, authFetch: AuthFetchWithResponse): Promise<void> => {
  const response = await authFetch<void>({
    path: `installations/${installationId}/files`,
    method: 'POST',
    payload: fileObject,
    endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
  })
  if (response.status !== StatusCodes.CREATED) {
    throw new UnexpectedApiResponseError(response.status)
  }
}
